import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Clock, Pencil, Plus, Send, Sparkles, X } from 'lucide-react';
import { client } from '@/lib/api';
import { useGmailStatus } from '@/components/GmailBanner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import StatusChip from '@/components/StatusChip';
import EditWeddingDialog, { type EditableWedding } from '@/components/EditWeddingDialog';
import AddCategoryDialog from '@/components/AddCategoryDialog';
import CouplePhotoButton from '@/components/CouplePhotoButton';
import TimelinePanel from '@/components/TimelinePanel';
import WeddingInboxPanel from '@/components/WeddingInboxPanel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDate, formatInstant } from '@/lib/date';
import { formatQuote } from '@/lib/format';
import {
  coupleShortName,
  formatWeddingHours,
  weddingLabelName,
  type InquiryStatus,
} from '@vendorops/shared';

/**
 * Tab labels in their own voice, deliberately NOT the global nav's (EVE-70).
 *
 * These used to be `label-caps text-xs` — the exact treatment the header links
 * wear. Sharing it was the bug: 12px uppercase is how this app says "app-level
 * nav, up in the glass bar", so three of them sitting in the content read as a
 * legend over the cards rather than as the control that moves between readings
 * of this wedding. A planner looking for the way back to the board clicked the
 * couple's name instead, because it was the only thing on screen that looked
 * like it belonged to the wedding rather than to the app.
 *
 * So: sentence case at the body size, which is what a tab looks like
 * everywhere else, and the state carried by weight and colour rather than by
 * the underline alone. Inactive recedes to muted and lifts on hover — the step
 * from `foreground/70` to `foreground` was too small to read as "elsewhere".
 * The active underline stays sage: still the only coloured thing in the strip.
 */
const TAB_CLASS =
  'flex-none px-0 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground data-[state=active]:font-semibold data-[state=active]:text-foreground data-[state=active]:after:bg-primary';

/**
 * Which view the URL is asking for.
 *
 * Only the two named views are spelled out: the board is the default, so an
 * absent param, a stale link and a typo all land on the same place rather than
 * on an empty page. `?view=vendors` is never written — the plain URL already
 * means it.
 */
const VIEW_PARAM = 'view';
const DEFAULT_VIEW = 'vendors';
const NAMED_VIEWS = new Set(['inbox', 'timeline']);

/**
 * Written by the create flow, read once here (EVE-56).
 *
 * It is a param rather than page state because the navigation that sets it is
 * the create dialog's, two routes away — and because it has to survive the
 * reload a planner may well do on a wedding they have never seen. Dismissing
 * takes it out of the URL, so it never survives twice.
 */
const CREATED_PARAM = 'created';

/**
 * The Wedding Hub (PRD §F2).
 *
 * Organized by category, not chronology, because that is how planners think —
 * "who's my florist," not "what happened Tuesday." Categories with no inquiries
 * render as an explicit gap rather than being omitted: the gap is the single
 * most useful thing on the page, and an inbox cannot show it to you.
 */
export default function WeddingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [editing, setEditing] = useState(false);
  const [addingCategory, setAddingCategory] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();

  // The open view lives in the URL, so a planner can send themselves the
  // timeline rather than a page they then have to click into. Replace, not push:
  // flicking between readings of one wedding is not a step of history each.
  const requested = params.get(VIEW_PARAM);
  const view = requested !== null && NAMED_VIEWS.has(requested) ? requested : DEFAULT_VIEW;
  const openView = (next: string) => {
    const nextParams = new URLSearchParams(params);
    if (NAMED_VIEWS.has(next)) nextParams.set(VIEW_PARAM, next);
    else nextParams.delete(VIEW_PARAM);
    setParams(nextParams, { replace: true });
  };

  // The first look at a wedding the planner just created.
  const firstLook = params.get(CREATED_PARAM) !== null;
  const dismissFirstLook = () => {
    const nextParams = new URLSearchParams(params);
    nextParams.delete(CREATED_PARAM);
    setParams(nextParams, { replace: true });
  };

  // Composing leads to sending, and sending needs a mailbox. Read from the
  // hook the rest of the app already uses rather than a second source.
  const { data: gmail } = useGmailStatus();
  const gmailConnected = gmail?.connected !== false;

  const { data: wedding, isLoading } = useQuery({
    queryKey: ['wedding', id],
    queryFn: async () => {
      const res = await client.api.weddings[':id'].$get({ param: { id: id! } });
      if (!res.ok) throw new Error('Failed to load wedding');
      return res.json();
    },
    enabled: !!id,
  });

  // The UI only ever offers this on an empty gap tile, but the server is
  // still the authority: a category can pick up an inquiry between the last
  // render and the click, and the 409 it sends back then is real, not a bug.
  const removeCategory = useMutation({
    mutationFn: async (categoryId: string) => {
      const res = await client.api.weddings[':id'].categories[':categoryId'].$delete({
        param: { id: id!, categoryId },
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? 'Failed to remove this category');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wedding', id] });
    },
    onError: (err: Error) => setRemoveError(err.message),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!wedding) return <p className="text-sm text-muted-foreground">Wedding not found.</p>;

  const started = wedding.board.filter((c) => c.inquiries.length > 0);
  const gaps = wedding.board.filter((c) => c.inquiries.length === 0);
  const hours = formatWeddingHours(wedding.startTime, wedding.endTime);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-3">
          <Link
            to="/weddings"
            className="label-caps text-xs text-muted-foreground hover:text-foreground"
          >
            ← Weddings
          </Link>
          {/* The portrait sits beside the facts, not above the back link: it
              belongs to the couple the heading names, and the grid the planner
              just came from showed them the same circle. It is also how the
              photo gets set — clicking it saves one, which is what the circle
              does here and not on the grid, where the same picture is a link
              into the wedding instead. */}
          <div className="flex items-start gap-4">
            <CouplePhotoButton
              weddingId={wedding.id}
              couple={wedding}
              photoKey={wedding.photoKey ?? null}
              className="w-16 shrink-0 sm:w-20"
            />
            <div className="space-y-2">
              <h1 className="display-tight text-3xl font-semibold">{coupleShortName(wedding)}</h1>
              <p className="text-sm text-muted-foreground">
                {formatDate(wedding.weddingDate)}
                {/* Hours belong beside the date — they are one fact about the
                    day, and a planner who just entered them should see them
                    land. Null when there is no start time, so nothing renders. */}
                {hours && ` · ${hours}`}
                {wedding.venueName && ` · ${wedding.venueName}`}
                {wedding.guestCountEstimate && ` · ~${wedding.guestCountEstimate} guests`}
              </p>
              <p className="text-xs text-muted-foreground">
                Gmail label <code>{weddingLabelName(wedding)}</code>
              </p>
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
          <Pencil className="size-3.5" />
          Edit
        </Button>
      </div>

      <EditWeddingDialog
        wedding={wedding as unknown as EditableWedding}
        open={editing}
        onOpenChange={setEditing}
      />

      {/* Three readings of one wedding — who is booked, what they have said,
          and how the day runs. The header stays above them all: a planner
          reading times needs the date and hours those times belong to still on
          screen. */}
      <Tabs value={view} onValueChange={openView} className="gap-6">
        {/* Sticky under the 64px glass header, and bled to the gutters so the
            cream reaches the full width of the strip as content passes beneath
            it (EVE-70). The timeline is a tall grid — the one reading of a
            wedding a planner scrolls far enough to lose the page header
            entirely, which left the whole wedding with no visible navigation
            of its own exactly when they wanted to leave it.

            Below the header's z-50 so the glass wins the overlap. */}
        <div className="sticky top-16 z-40 -mx-6 bg-background px-6">
          {/* Underlined rather than a filled segmented control: the only other
              chrome on this page is white cards on cream, and a champagne pill
              would be the loudest thing on it.

              The hairline carries the strip across the full width so the three
              labels read as one band of navigation. The active marker is the
              same line in sage, which is why the triggers are pinned flush to
              the bottom of the list: the two have to be the same rail, or the
              near-miss reads as a mistake.

              Both overrides are `!`-marked on purpose. The vendored trigger
              sets its height and its marker offset through `group-data-*`
              variants, whose attribute selector outranks a plain utility — so
              the unmarked versions of these two lose the cascade and the sage
              line floats 5px under the hairline. */}
          <TabsList
            variant="line"
            className="w-full justify-start gap-6 border-b p-0 [&>*]:h-full! [&>*]:after:bottom-0!"
          >
            <TabsTrigger value="vendors" className={TAB_CLASS}>
              Vendors
            </TabsTrigger>
            {/* Between the two: mail is the middle reading of a wedding, between
                who is booked and how the day runs. */}
            <TabsTrigger value="inbox" className={TAB_CLASS}>
              Inbox
            </TabsTrigger>
            <TabsTrigger value="timeline" className={TAB_CLASS}>
              Timeline
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="vendors" className="space-y-8">
          {/* ── Categories with inquiries out ───────────────────────────────────────── */}
          <div className="space-y-4">
            {started.map((row) => (
              <Card key={row.category} className="rounded-3xl bg-card p-5 shadow-ambient">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <h2 className="display-tight text-lg font-semibold">{row.category}</h2>
                  {gmailConnected ? (
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/weddings/${wedding.id}/compose?category=${encodeURIComponent(row.category)}`}>
                        <Plus className="size-3.5" />
                        Add more
                      </Link>
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" disabled title="Connect Gmail first">
                      <Plus className="size-3.5" />
                      Add more
                    </Button>
                  )}
                </div>

                <div className="divide-y divide-border">
                  {row.inquiries.map((inq) => (
                    <Link
                      key={inq.id}
                      to={`/inquiries/${inq.id}`}
                      className="flex flex-wrap items-center justify-between gap-3 py-3 transition-colors hover:bg-[var(--color-surface-container-high)]"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{inq.vendorName}</p>
                        <p className="text-xs text-muted-foreground">
                          <SendStateLine inquiry={inq} />
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {/* One amount, not two: `quoteSource` says whose it is, so
                            an unconfirmed figure is the same number worn lightly —
                            tilde, muted, smaller — rather than a second column. A
                            quote that priced several packages is a span, and the
                            tilde qualifies the whole of it. */}
                        {inq.quoteAmount && inq.quoteSource === 'extracted' && (
                          <span
                            className="text-xs whitespace-nowrap text-muted-foreground"
                            title="Read from a reply — open the inquiry to check it"
                          >
                            ~
                            {formatQuote({
                              amount: inq.quoteAmount,
                              amountHigh: inq.quoteAmountHigh,
                              currency: inq.quoteCurrency,
                            })}
                          </span>
                        )}
                        {inq.quoteAmount && inq.quoteSource !== 'extracted' && (
                          <span className="text-sm font-medium whitespace-nowrap">
                            {formatQuote({
                              amount: inq.quoteAmount,
                              amountHigh: inq.quoteAmountHigh,
                              currency: inq.quoteCurrency,
                            })}
                          </span>
                        )}
                        <StatusChip
                          status={inq.status as InquiryStatus}
                          source={inq.statusSource as 'system' | 'user'}
                        />
                      </div>
                    </Link>
                  ))}
                </div>
              </Card>
            ))}
          </div>

          {/* ── The gaps ────────────────────────────────────────────────────────────── */}
          {/* The header (and its Add category button) renders even with zero gaps —
              a fully populated board still needs a way to attach a category that
              has no inquiries yet. Only the tile grid is gated on there being any. */}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              {/* At zero gaps "Nothing sent yet (0)" reads like a complaint about a
                  fully populated board, so the heading only exists when it is
                  actually describing something — an empty <h2> is announced as a
                  blank heading. The spacer keeps the button flushed right; the
                  button stays either way. */}
              {gaps.length > 0 ? (
                <h2 className="label-caps text-xs text-muted-foreground">
                  Nothing sent yet ({gaps.length})
                </h2>
              ) : (
                <span />
              )}
              <Button variant="outline" size="sm" onClick={() => setAddingCategory(true)}>
                <Plus className="size-3.5" />
                Add category
              </Button>
            </div>
            {removeError && <p className="text-xs text-destructive">{removeError}</p>}
            {gaps.length > 0 && (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {gaps.map((row) => (
                  // relative + group: the remove button is an absolutely-positioned
                  // sibling of the Link, not a child — a <button> nested inside an
                  // <a> is invalid HTML and its click would race the navigation.
                  <div key={row.category} className="group relative">
                    {gmailConnected ? (
                      <Link
                        to={`/weddings/${wedding.id}/compose?category=${encodeURIComponent(row.category)}`}
                        className="flex items-center justify-between gap-2 rounded-2xl border border-dashed border-border px-4 py-3 text-sm transition-colors hover:border-primary hover:bg-[var(--color-surface-container-high)]"
                      >
                        <span>{row.category}</span>
                        <Send className="size-3.5 text-muted-foreground" />
                      </Link>
                    ) : (
                      <div
                        title="Connect Gmail first"
                        aria-disabled
                        className="flex cursor-not-allowed items-center justify-between gap-2 rounded-2xl border border-dashed border-border px-4 py-3 text-sm opacity-50"
                      >
                        <span>{row.category}</span>
                        <Send className="size-3.5 text-muted-foreground" />
                      </div>
                    )}
                    {/* Only a board member with zero inquiries can be removed —
                        categoryId is null for a rescued orphan, and there is
                        nothing to detach there. */}
                    {row.categoryId !== null && (
                      <button
                        type="button"
                        aria-label={`Remove ${row.category}`}
                        disabled={removeCategory.isPending}
                        onClick={() => {
                          setRemoveError(null);
                          removeCategory.mutate(row.categoryId!);
                        }}
                        className="absolute right-2 top-2 rounded-full p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
                      >
                        <X className="size-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mounted unconditionally (not nested in the gaps.length > 0 branch
              above) so opening it doesn't depend on there being any gap tiles. */}
          <AddCategoryDialog
            weddingId={wedding.id}
            onBoard={wedding.board.map((r) => r.category)}
            open={addingCategory}
            onOpenChange={setAddingCategory}
          />
        </TabsContent>

        <TabsContent value="inbox">
          <WeddingInboxPanel
            weddingId={wedding.id}
            // Same expression InquiryDetailPage uses, off the useGmailStatus()
            // call already on this page: the link out needs the mailbox, not
            // the connection.
            gmailAddress={gmail?.connected ? gmail.email : null}
          />
        </TabsContent>

        <TabsContent value="timeline" className="space-y-6">
          {firstLook && (
            <DraftedDayNotice
              couple={coupleShortName(wedding)}
              onDismiss={dismissFirstLook}
            />
          )}
          {/* The hours the whole timeline is placed against are set in the edit
              dialog this page already owns, so the panel asks for it by name
              rather than growing a second way to enter them. */}
          <TimelinePanel weddingId={wedding.id} onEditHours={() => setEditing(true)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * What a planner sees first on a wedding they just made (EVE-56).
 *
 * Its whole job is to say that the times below are a draft of the planner's
 * own ladder and not a schedule anyone agreed to — and then to say where this
 * tab is, because the next visit to this wedding opens on the board and a
 * planner who found the timeline by being sent here has never clicked the tab.
 *
 * In the sage container rather than the warning sand: nothing is wrong, and a
 * tone the app spends on sync failures would read as one. It is dismissible
 * because it is an announcement, and an announcement that cannot be ended is
 * an announcement the planner reads around for the life of the wedding.
 */
function DraftedDayNotice({ couple, onDismiss }: { couple: string; onDismiss: () => void }) {
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-[var(--radius)] bg-[var(--color-primary-container)] px-4 py-3 text-[var(--color-accent-foreground)]"
    >
      <Sparkles aria-hidden className="mt-0.5 size-4 shrink-0 text-[var(--color-primary-dim)]" />
      <p className="flex-1 text-sm leading-relaxed">
        We drafted {couple}&rsquo;s day from your timeline template. Review the times and change
        anything that is off — it is a starting point, not a commitment. The{' '}
        <span className="font-medium">Timeline</span> tab brings you back to it any time.
      </p>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Dismiss"
        onClick={onDismiss}
        className="-my-1 -mr-2 size-8 shrink-0 text-[var(--color-accent-foreground)] hover:bg-[var(--color-primary)]/10"
      >
        <X className="size-4" />
      </Button>
    </div>
  );
}

/**
 * One line that says where this inquiry actually is. Send state outranks
 * inquiry status here: a planner looking at a failed send needs to see the
 * failure, not the word "draft".
 */
function SendStateLine({
  inquiry,
}: {
  inquiry: {
    sendState: string;
    sendError: string | null;
    scheduledSendAt: string | null;
    sentAt: string | null;
    lastInboundAt: string | null;
  };
}) {
  if (inquiry.sendState === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 text-destructive">
        <AlertCircle className="size-3" />
        Send failed: {inquiry.sendError ?? 'unknown error'}
      </span>
    );
  }

  if (inquiry.sendState === 'queued' || inquiry.sendState === 'sending') {
    return (
      <span className="inline-flex items-center gap-1">
        <Clock className="size-3" />
        Queued to send
        {inquiry.scheduledSendAt &&
          ` ${formatInstant(inquiry.scheduledSendAt, { hour: 'numeric', minute: '2-digit' })}`}
      </span>
    );
  }

  if (inquiry.lastInboundAt) {
    return <>Replied {formatInstant(inquiry.lastInboundAt, { month: 'short', day: 'numeric' })}</>;
  }

  if (inquiry.sentAt) {
    const days = Math.floor((Date.now() - new Date(inquiry.sentAt).getTime()) / 86_400_000);
    return (
      <>
        Sent {formatInstant(inquiry.sentAt, { month: 'short', day: 'numeric' })}
        {days > 0 && ` · ${days} ${days === 1 ? 'day' : 'days'} ago, no reply`}
      </>
    );
  }

  return <>Not sent</>;
}
